import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { format } from 'date-fns';
import { createEvents, type EventAttributes } from 'ics';
import type { Appointment } from '../entities/appointment.js';
import { ParticipationState } from '../enums/enums.js';

interface WorkshopICSInput {
  name: string;
  appointments: NarrowedEntityData<Appointment, { start: 1; name: 1; end: 1; location: 1 }>[];
  courseLocation?: Maybe<string>;
  participationState: ParticipationState;
}

export function generateICSAttachment(workshops: WorkshopICSInput[]) {
  const events: EventAttributes[] = [];

  for (const workshop of workshops) {
    if (!workshop.appointments) continue;

    for (const appointment of workshop.appointments) {
      if (!appointment.start || !(appointment.start instanceof Date)) continue;

      const title = `${workshop.name}${appointment.name ? ` - ${appointment.name}` : ''}`;
      const location = appointment.location ?? workshop.courseLocation ?? '';

      const durationMinutes = (() => {
        if (!appointment.end || !(appointment.end instanceof Date)) return 30;
        return Math.round((appointment.end.getTime() - appointment.start.getTime()) / 1000 / 60);
      })();

      events.push({
        title,
        location,
        start: format(appointment.start, "yyyyMMdd'T'HHmm'00Z'"),
        duration: { minutes: durationMinutes },
        status: workshop.participationState === ParticipationState.in ? 'CONFIRMED' : 'TENTATIVE',
        calName: 'Buchungsheld Workshops',
      });
    }
  }

  if (events.length === 0) return null;

  const { error, value } = createEvents(events);
  if (error || !value) {
    logger.warn({ error }, 'failed to generate ICS attachment');
    return null;
  }

  return { filename: 'workshops.ics', content: value, contentType: 'text/calendar' };
}
